import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';

type Lang = 'nl' | 'en';

const labels: Record<Lang, string> = {
  nl: 'Nederlands',
  en: 'English',
};

export const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState<Lang>(() => (i18n.language?.startsWith('nl') ? 'nl' : 'en'));
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onEsc); };
  }, []);

  const switchTo = async (l: Lang) => {
    if (l === lang) return;
    setLang(l);
    try { await i18n.changeLanguage(l); } catch {}
    try { localStorage.setItem('fit_lang', l); } catch {}
    setOpen(false);
  };

  const otherLangs: Lang[] = (['nl','en'] as Lang[]).filter(l => l !== lang) as Lang[];

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium shadow-sm hover:bg-gray-50"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Globe size={16} />
        <span>{labels[lang]}</span>
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-1 w-36 rounded-md border border-gray-200 bg-white shadow-lg z-10">
          {otherLangs.map(l => (
            <button
              key={l}
              role="menuitem"
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
              onClick={() => switchTo(l)}
            >
              {labels[l]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
